import React, { createRef, RefObject } from "react";
import { CSSTransition } from "react-transition-group";

import { YoutubeBlockModel } from "@courses-models/course";
import { asyncTimeout } from "@use/async-timeout";
import { hrtime } from "@use/universal-hrtime";

import { colors } from "@apps/viewer/src/ts/config";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

import { Block } from "@apps/viewer/src/ts/components/block/Block";

type YoutubeBlockProps = {
  /**
   * A reference to the store that's backing the application lifecycle state.
   */
  store: Store;

  /**
   * A reference to the model representing the block that's currently being
   * rendered.
   */
  model: YoutubeBlockModel;
};

type YoutubeBlockState = {
  isLoaded: boolean;
};

/**
 * Specifies for how long the player at least should be waiting before appearing
 */
const MIN_LOADING_TIME = 300;

/**
 * Renders embedded Youtube video players.
 */
class YoutubeBlockUnwrapped extends Block<
  YoutubeBlockProps,
  YoutubeBlockState
> {
  /**
   * Setup initial state
   */
  public state: YoutubeBlockState = {
    isLoaded: false,
  };

  /**
   * Contains a timestamp of when the API became available in order to determine
   * how long we should wait before showing the video
   */
  private apiReadyAt: number = hrtime();

  /**
   * Specifies whether or not the component is currently mounted
   */
  private componentIsMounted: boolean = true;

  private throbberRef: RefObject<HTMLDivElement | null> = createRef();

  /**
   * Once the component mounts, begin loading the associated video
   */
  public componentDidMount(): void {
    // tslint:disable-next-line no-any
    if (!(window as any).onYouTubeIframeAPIReady) {
      // If our custom IframeAPIReady callback hasn't been attached to the
      // window yet, then that means the API isn't available yet.
      // Hence fetch that firstly.
      this.injectAPI();
    } else {
      // ... else the API will be available and we can begin loading the
      // video immediately
      this.onAPIReady();
    }
  }

  /**
   * Indicate that component is umounted once the unmount lifecycle event
   * occurs
   */
  public componentWillUnmount(): void {
    this.componentIsMounted = false;
  }

  /**
   * Renderer that'll render the player associated with the block
   */
  protected renderContent(): React.JSX.Element {
    return (
      <div className="player-aspect-ratio">
        <div
          className={`player-wrapper ${this.state.isLoaded ? "loaded" : ""}`}
        >
          <div id="player-element" />
          {this.renderThrobber()}
        </div>

        <style jsx>{`
          /**
                     * Create a 16:9 aspect ratio for the video to be contained
                     * within since this is the aspect ratio of videos
                     * on YouTube
                     */
          .player-aspect-ratio {
            /** Setup spacing between title and wrapper */
            margin-top: 35px;

            /** Create aspect-ratio */
            position: relative;
            width: 100%;
            height: 0;
            padding-bottom: 56.25%;
          }

          .player-wrapper {
            /** Let the actual wrapper fill the aspect ratio container */
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;

            /** Background-color will be visible while video is loading */
            background-color: #f7f7f7;

            & #player-element {
              /** Let player expand to fill container */
              width: 100%;
              height: 100%;

              /** Prepare enter transition */
              opacity: 0;
              transition: opacity 0.3s linear;
            }

            /** Once the player is loaded, then it should be visible */
            &.loaded {
              & #player-element {
                opacity: 1;
              }
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll render a throbber that'll be displayed before the player
   * is ready
   */
  protected renderThrobber(): React.JSX.Element {
    return (
      <CSSTransition
        in={!this.state.isLoaded}
        unmountOnExit
        classNames="player-throbber"
        timeout={200}
        enter={false}
        nodeRef={this.throbberRef}
      >
        <React.Fragment>
          <div ref={this.throbberRef} className="player-throbber" />

          <style jsx>{`
            .player-throbber {
              /** Position throbber in the middle of the player wrapper */
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);

              /** Fixate dimensions of throbber */
              height: 48px;
              width: 48px;

              /** Should be visible by default */
              opacity: 1;

              /** Prepare exit transition */
              &.player-throbber-exit-active {
                opacity: 0;
                transition: opacity 0.2s linear;
              }

              /** Render the actual throbber using a pseudo element */
              &::before {
                /** Force rendering */
                content: "";

                /** Fill wrapper */
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;

                /** Ensure borders doesn't overflow */
                box-sizing: border-box;

                /** Render the actual throbber */
                border-radius: 50%;
                border: 3px solid rgba(220, 67, 32, 0.25);
                border-left-color: ${colors.orange};

                /** ... and make it spin forever */
                animation: spin 0.9s linear infinite;
              }
            }

            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}</style>
        </React.Fragment>
      </CSSTransition>
    );
  }

  /**
   * Method that'll be called once the Youtube Player has properly loaded
   * in order to display the Youtube API
   */
  protected onPlayerReady = async (): Promise<void> => {
    // Determine for how long we should wait before displaying the video
    // based on the specified min loading time.
    const elapsedTime = hrtime() - this.apiReadyAt;

    // We add 200ms no matter when to ensure that placeholder image of player
    // has gotten a change to proplery render
    const timeout = Math.max(0, MIN_LOADING_TIME - elapsedTime) + 200;

    await asyncTimeout(timeout);

    if (this.componentIsMounted) {
      this.setState({ isLoaded: true });
    }
  };

  /**
   * Helper that'll inject the Youtube Embed Player API into the page
   */
  protected injectAPI(): void {
    // Bind required global method that'll be executed once the API has loaded
    // tslint:disable-next-line no-any
    (window as any).onYouTubeIframeAPIReady = () => {
      this.onAPIReady();
    };

    // Then inject the tag that'll load the API before the first script on
    // the page in order to keep scripts together
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    (firstScriptTag.parentNode as HTMLElement).insertBefore(
      tag,
      firstScriptTag
    );
  }

  /**
   * Method that'll be called once the API is ready in order to instantiate a
   * player
   */
  protected onAPIReady(): void {
    this.apiReadyAt = hrtime();

    const player = new YT.Player("player-element", {
      videoId: this.props.model.videoId,
    });

    this.onPlayerReady();
  }
}

// tslint:disable-next-line variable-name
export const YoutubeBlock = injectStore(YoutubeBlockUnwrapped);
